/**
 * PIO, critical incident notices and anonymous tips.
 *   /pio                     media releases and case statements (write → command staff approve → release)
 *   /pio/notices             every critical incident notice; /pio/notices/new starts one (from a CAD call)
 *   /pio/recipients          who gets critical notices, and by email / text
 *   /pio/settings            the public tip page, the Jotform link, the CAD link
 *   /tips                    the tip queue
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { Icon } from '../shared/icons.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, KPI, when } from './common.tsx';

type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'info';
const back = (label: string, to: string, navigate: (p: string) => void) => (
  <button className="btn link" onClick={() => navigate(at(to))}>
    {label}
  </button>
);
const localInput = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(+d - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

// ─── Critical incident notices ──────────────────────────────────────────────
export function NoticesPage() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/notices'), []);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="PIO"
        title="Critical incident notices"
        sub="Newsworthy incidents, sent straight to the sheriff and command staff by email and text."
        actions={
          <button className="btn danger-solid" onClick={() => navigate(at('/pio/notices/new'))}>
            <Icon name="attention" size={16} /> New critical notice
          </button>
        }
      />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.notices.length ? (
        <EmptyState icon="attention" title="No critical notices yet" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Notice</th>
                <th>What</th>
                <th>Where</th>
                <th>From</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.notices.map((n: any) => (
                <tr key={n.id} className="clickable" onClick={() => navigate(at(`/pio/notices/${n.id}`))}>
                  <td className="mono">{n.number}</td>
                  <td>
                    <strong>{n.title}</strong>
                    <div className="small muted">{r.data.kinds[n.kind] ?? n.kind}</div>
                  </td>
                  <td>{n.location ?? '—'}</td>
                  <td>{n.created_by_name}</td>
                  <td>{n.status === 'sent' ? <Badge tone="ok">Sent {when(n.sent_at)}</Badge> : <Badge tone="warn">Draft — not sent</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function NewNotice() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/cad/recent'), []);
  const a = useAction();
  const start = (cadCallId: string | null) =>
    a.run(async () => {
      const n = await api.post<any>('/api/notices', cadCallId ? { cadCallId } : { kind: 'other' });
      navigate(at(`/pio/notices/${n.id}`), true);
    });
  return (
    <div className="ops stack">
      <PageHeader crumb={back('Critical notices', '/pio/notices', navigate)} title="New critical incident notice" sub="Pick the CAD call to start from — what CAD already knows is filled in — then add the rest and send." />
      <ErrorBox error={r.error ?? a.error} />
      <div>
        <button className="btn" disabled={a.busy} onClick={() => start(null)}>
          Start without a CAD call
        </button>
      </div>
      {!r.data ? (
        !r.error && <Spinner />
      ) : !r.data.calls.length ? (
        <Notice>{r.data.connected ? 'No CAD calls in the last 24 hours.' : 'CAD isn’t sending calls to BlueHQ yet, so start without one. (An administrator can connect CAD under PIO → Settings.)'}</Notice>
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>CAD call</th>
                <th>Received</th>
                <th>Nature</th>
                <th>Address</th>
                <th>Units</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {r.data.calls.map((c: any) => (
                <tr key={c.id}>
                  <td className="mono">{c.call_number}</td>
                  <td>{when(c.received_at)}</td>
                  <td>{c.nature}</td>
                  <td>{[c.address, c.city].filter(Boolean).join(', ')}</td>
                  <td>{c.units?.join(', ')}</td>
                  <td>
                    <button className="btn primary small" disabled={a.busy} onClick={() => start(c.id)}>
                      Use this call
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function NoticeDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/notices/${id}`), [id]);
  const a = useAction();
  const [f, setF] = useState<any>(null);
  const [upd, setUpd] = useState('');
  const v = r.data;
  useEffect(() => {
    if (v && !f) setF({ ...v.notice, occurredAt: localInput(v.notice.occurred_at) });
  }, [v, f]);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const payload = () => ({
    kind: f.kind, title: f.title, occurredAt: f.occurredAt ? new Date(f.occurredAt).toISOString() : null, location: f.location || null, cadNumber: f.cad_number || null, caseNumber: f.case_number || null,
    summary: f.summary || null, injuries: f.injuries || null, suspect: f.suspect || null, units: f.units || null, media: f.media || null, needs: f.needs || null,
  });
  const save = () => a.run(async () => (await api.post(`/api/notices/${id}`, payload()), r.reload()));
  const send = (e: FormEvent) => {
    e.preventDefault();
    if (!confirm(`Send this notice now to ${v.willGoTo.length} ${v.willGoTo.length === 1 ? 'person' : 'people'}?`)) return;
    a.run(async () => {
      await api.post(`/api/notices/${id}`, payload());
      await api.post(`/api/notices/${id}/send`, {});
      setF(null);
      r.reload();
    });
  };
  const addUpdate = () => a.run(async () => (await api.post(`/api/notices/${id}/update`, { text: upd }), setUpd(''), r.reload()));
  if (!v || !f) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const n = v.notice;
  const sentRounds = [...new Set(v.sends.map((s: any) => s.update_no))] as number[];
  return (
    <div className="ops stack">
      <PageHeader crumb={back('Critical notices', '/pio/notices', navigate)} title={`${n.number} · ${n.title}`} sub={v.kinds[n.kind]} actions={n.status === 'sent' ? <Badge tone="ok">Sent {when(n.sent_at)} by {n.sent_by_name}</Badge> : <Badge tone="warn">Not sent yet</Badge>} />
      <ErrorBox error={a.error} />
      {v.canEdit ? (
        <form className="stack" onSubmit={send}>
          <Notice tone="warn">Don’t put criminal history (NCIC / SLED results) in a notice — it goes out by email and text.</Notice>
          <Card title="What happened">
            <div className="stack">
              <div className="grid-3">
                <label>
                  Type of incident
                  <select value={f.kind} onChange={set('kind')}>
                    {Object.entries(v.kinds).map(([k, l]) => (
                      <option key={k} value={k}>
                        {l as string}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Headline
                  <input value={f.title} onChange={set('title')} required maxLength={200} />
                </label>
                <label>
                  When
                  <input type="datetime-local" value={f.occurredAt} onChange={set('occurredAt')} />
                </label>
                <label>
                  Where
                  <input value={f.location ?? ''} onChange={set('location')} maxLength={300} />
                </label>
                <label>
                  CAD #
                  <input value={f.cad_number ?? ''} onChange={(e) => setF({ ...f, cad_number: e.target.value })} maxLength={40} />
                </label>
                <label>
                  Case #
                  <input value={f.case_number ?? ''} onChange={(e) => setF({ ...f, case_number: e.target.value })} maxLength={40} />
                </label>
              </div>
              <label>
                What happened *
                <textarea rows={5} value={f.summary ?? ''} onChange={set('summary')} required placeholder="The short version command staff needs right now." />
              </label>
              <div className="grid-2">
                <label>
                  Injuries / deaths
                  <input value={f.injuries ?? ''} onChange={set('injuries')} />
                </label>
                <label>
                  Suspect(s)
                  <input value={f.suspect ?? ''} onChange={set('suspect')} placeholder="In custody? Outstanding? Description" />
                </label>
                <label>
                  Units / agencies on scene
                  <input value={f.units ?? ''} onChange={set('units')} />
                </label>
                <label>
                  Media
                  <input value={f.media ?? ''} onChange={set('media')} placeholder="Media on scene? Calls coming in?" />
                </label>
              </div>
              <label>
                What you need from command staff
                <input value={f.needs ?? ''} onChange={set('needs')} placeholder="e.g. CID callout, PIO, more units, the Sheriff on scene" />
              </label>
            </div>
          </Card>
          <Card title="It will go to">
            {!v.willGoTo.length ? (
              <Notice tone="bad">No one is set up to get critical notices. An administrator sets that up under PIO → Notice recipients.</Notice>
            ) : (
              <ul className="cn-to">
                {v.willGoTo.map((p: any) => (
                  <li key={p.name}>
                    <strong>{p.name}</strong> <span className="small muted">{[p.email && 'email', p.text && 'text', 'BlueHQ'].filter(Boolean).join(' + ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <div className="row gap">
            <button className="btn danger-solid big" disabled={a.busy || !v.willGoTo.length}>
              {a.busy ? 'Sending…' : 'Send critical notice'}
            </button>
            <button type="button" className="btn" disabled={a.busy} onClick={save}>
              Save, send later
            </button>
          </div>
        </form>
      ) : (
        <>
          <Card title="Notice">
            <dl className="kv">
              <dt>When</dt>
              <dd>{when(n.occurred_at)}</dd>
              <dt>Where</dt>
              <dd>{n.location ?? '—'}</dd>
              {n.cad_number && (
                <>
                  <dt>CAD #</dt>
                  <dd className="mono">{n.cad_number}</dd>
                </>
              )}
              {n.case_number && (
                <>
                  <dt>Case #</dt>
                  <dd className="mono">{n.case_number}</dd>
                </>
              )}
              <dt>What happened</dt>
              <dd className="pre">{n.summary}</dd>
              {n.injuries && (
                <>
                  <dt>Injuries</dt>
                  <dd>{n.injuries}</dd>
                </>
              )}
              {n.suspect && (
                <>
                  <dt>Suspect(s)</dt>
                  <dd>{n.suspect}</dd>
                </>
              )}
              {n.units && (
                <>
                  <dt>Units</dt>
                  <dd>{n.units}</dd>
                </>
              )}
              {n.media && (
                <>
                  <dt>Media</dt>
                  <dd>{n.media}</dd>
                </>
              )}
              {n.needs && (
                <>
                  <dt>Needs</dt>
                  <dd>{n.needs}</dd>
                </>
              )}
              <dt>From</dt>
              <dd>
                {n.created_by_name}
                {n.created_by_call ? ` (${n.created_by_call})` : ''}
              </dd>
            </dl>
          </Card>
          {n.updates?.length > 0 && (
            <Card title="Updates">
              <ul className="cn-updates">
                {n.updates.map((u: any) => (
                  <li key={u.no}>
                    <strong>Update {u.no}</strong> <span className="small muted">· {u.by} · {when(u.at)}</span>
                    <div className="pre">{u.text}</div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {v.canUpdate && (
            <Card title="Send an update to the same people">
              <div className="stack sm">
                <textarea rows={2} value={upd} onChange={(e) => setUpd(e.target.value)} placeholder="e.g. Suspect in custody. Road reopened." aria-label="Update" />
                <div>
                  <button className="btn primary" disabled={a.busy || !upd.trim()} onClick={addUpdate}>
                    Send update
                  </button>
                </div>
              </div>
            </Card>
          )}
          {sentRounds.length > 0 && (
            <Card title="Sent to">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Who</th>
                      <th>How</th>
                      <th>To</th>
                      <th>When</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.sends.map((s: any, i: number) => (
                      <tr key={i}>
                        <td>
                          {s.name}
                          {s.update_no > 0 && <span className="small muted"> · update {s.update_no}</span>}
                        </td>
                        <td>{{ email: 'Email', text: 'Text', app: 'BlueHQ message' }[s.channel as 'email']}</td>
                        <td className="small">{s.address ?? ''}</td>
                        <td>{when(s.at)}</td>
                        <td>
                          {s.channel === 'app' ? <Badge tone="ok">In their BlueHQ messages</Badge> : <Badge tone={({ sent: 'ok', queued: 'info', logged: 'warn', failed: 'bad' } as Record<string, Tone>)[s.status] ?? 'neutral'}>{({ sent: 'Delivered to the service', queued: 'Sending', logged: 'Recorded (no service connected)', failed: 'Failed' } as Record<string, string>)[s.status] ?? s.status}</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export function RecipientsPage() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/notices/recipients'), []);
  return (
    <div className="ops stack">
      <PageHeader crumb={back('Critical notices', '/pio/notices', navigate)} title="Who gets critical notices" sub="The sheriff and command staff. Each person chooses email, text or both." />
      <ErrorBox error={r.error} />
      {r.data?.textMode === 'gateway' && (
        <Notice>
          Texts go out through the phone carrier’s email-to-text service, which can be slow and doesn’t work for AT&T. For dependable texts, connect a text service (Twilio) on the server.
        </Notice>
      )}
      {!r.data ? (
        !r.error && <Spinner />
      ) : (
        r.data.people.map((p: any) => <RecipientRow key={p.id} p={p} canEdit={r.data.canEdit} carriers={r.data.carriers} gateway={r.data.textMode === 'gateway'} onSaved={r.reload} />)
      )}
    </div>
  );
}

function RecipientRow({ p, canEdit, carriers, gateway, onSaved }: { p: any; canEdit: boolean; carriers: any[]; gateway: boolean; onSaved: () => void }) {
  const [f, setF] = useState({ receivesCritical: p.receives_critical, byEmail: p.by_email, byText: p.by_text, email: p.email ?? '', cell: p.cell ?? '', carrier: p.carrier ?? '' });
  const [saved, setSaved] = useState(false);
  const a = useAction();
  const save = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post(`/api/notices/recipients/${p.id}`, { ...f, email: f.email || null, cell: f.cell || null, carrier: f.carrier || null });
      setSaved(true);
      onSaved();
    });
  };
  return (
    <form className={`card cn-person${f.receivesCritical ? ' on' : ''}`} onSubmit={save} aria-label={p.name}>
      <div className="cn-person-h">
        <div>
          <strong>{p.name}</strong>
          {p.title && <span className="small muted"> · {p.title}</span>}
        </div>
        <label className="check">
          <input type="checkbox" checked={f.receivesCritical} disabled={!canEdit} onChange={(e) => setF({ ...f, receivesCritical: e.target.checked })} /> Gets critical notices
        </label>
      </div>
      {(f.receivesCritical || !canEdit) && (
        <div className="grid-4">
          <label className="check">
            <input type="checkbox" checked={f.byEmail} onChange={(e) => setF({ ...f, byEmail: e.target.checked })} /> By email
          </label>
          <label>
            Email
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder={p.account_email ?? 'name@agency.gov'} />
          </label>
          <label className="check">
            <input type="checkbox" checked={f.byText} onChange={(e) => setF({ ...f, byText: e.target.checked })} /> By text
          </label>
          <label>
            Cell
            <input value={f.cell} onChange={(e) => setF({ ...f, cell: e.target.value })} inputMode="tel" placeholder="(803) 555-0100" />
          </label>
          {gateway && (
            <label>
              Carrier
              <select value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })}>
                <option value="">Choose…</option>
                {carriers.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
      <ErrorBox error={a.error} />
      <div className="row gap">
        <button className="btn small" disabled={a.busy}>
          Save
        </button>
        {saved && <span className="small muted">Saved.</span>}
      </div>
    </form>
  );
}

// ─── PIO: releases and statements ───────────────────────────────────────────
const PIO_STATUS: Record<string, [string, Tone]> = { draft: ['Draft', 'neutral'], pending: ['Waiting for approval', 'warn'], returned: ['Sent back', 'bad'], approved: ['Approved', 'ok'], released: ['Released', 'info'] };

export function PioPage() {
  const { navigate } = useRouter();
  const [tab, setTab] = useState<'all' | 'pending' | 'release' | 'statement'>('all');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState<null | 'release' | 'statement'>(null);
  const qs = tab === 'pending' ? 'status=pending' : tab === 'all' ? '' : `kind=${tab}`;
  const r = useLoad(() => api.get<any>(`/api/pio?${qs}&q=${encodeURIComponent(q.trim())}`), [qs, q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="PIO"
        title="Media releases & statements"
        sub="Written by the PIO, approved by command staff, then released."
        actions={
          r.data?.canWrite && (
            <>
              <button className="btn" onClick={() => setAdding('statement')}>
                New case statement
              </button>
              <button className="btn primary" onClick={() => setAdding('release')}>
                New media release
              </button>
            </>
          )
        }
      />
      {adding && <NewPioItem kind={adding} onCancel={() => setAdding(null)} />}
      {r.data && (
        <div className="ops-kpis">
          <KPI n={r.data.counts.pending} label="Waiting for approval" tone={r.data.counts.pending ? 'warn' : undefined} onClick={() => setTab('pending')} on={tab === 'pending'} />
          <KPI n={r.data.counts.drafts} label="Drafts / sent back" />
          <KPI n={r.data.counts.approved} label="Approved, not released" />
        </div>
      )}
      <div className="row between wrap gap">
        <div className="seg-ctl" role="tablist" aria-label="Which">
          {(['all', 'pending', 'release', 'statement'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
              {{ all: 'All', pending: 'To approve', release: 'Media releases', statement: 'Case statements' }[k]}
            </button>
          ))}
        </div>
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title, case #, text…" aria-label="Search" />
      </div>
      {r.data?.newsroomPath && (
        <p className="small muted">
          Public newsroom page:{' '}
          <a href={r.data.newsroomPath} target="_blank" rel="noreferrer">
            {location.host}
            {r.data.newsroomPath}
          </a>
        </p>
      )}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.items.length ? (
        <EmptyState icon="pen" title="Nothing here yet" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Type</th>
                <th>Case</th>
                <th>Written by</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.items.map((x: any) => (
                <tr key={x.id} className="clickable" onClick={() => navigate(at(`/pio/${x.id}`))}>
                  <td className="mono">{x.number}</td>
                  <td>
                    <strong>{x.title}</strong>
                    <div className="small muted">Updated {when(x.updated_at)}</div>
                  </td>
                  <td>{x.kind === 'release' ? 'Media release' : 'Case statement'}</td>
                  <td className="mono">{x.case_number ?? ''}</td>
                  <td>{x.written_by_name}</td>
                  <td>
                    <Badge tone={PIO_STATUS[x.status]![1]}>{PIO_STATUS[x.status]![0]}</Badge>
                    {x.public && <div className="small muted">On the newsroom page</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewPioItem({ kind, onCancel }: { kind: 'release' | 'statement'; onCancel: () => void }) {
  const { navigate } = useRouter();
  const [f, setF] = useState({ title: '', caseNumber: '', body: '' });
  const a = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      const r = await api.post<any>('/api/pio', { kind, title: f.title, caseNumber: f.caseNumber || null, body: f.body });
      navigate(at(`/pio/${r.id}`));
    });
  };
  return (
    <Card title={kind === 'release' ? 'New media release' : 'New statement on a case'}>
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={a.error} />
        <div className="grid-2">
          <label>
            Title
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required maxLength={200} />
          </label>
          <label>
            Case # {kind === 'statement' ? '*' : '(optional)'}
            <input value={f.caseNumber} onChange={(e) => setF({ ...f, caseNumber: e.target.value })} required={kind === 'statement'} maxLength={40} />
          </label>
        </div>
        <label>
          {kind === 'release' ? 'Release' : 'Statement'}
          <textarea rows={8} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
        </label>
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            Save draft
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

export function PioItem({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/pio/${id}`), [id]);
  const a = useAction();
  const [f, setF] = useState<any>(null);
  const [note, setNote] = useState('');
  const [pub, setPub] = useState(true);
  const v = r.data;
  useEffect(() => {
    if (v) setF({ title: v.item.title, body: v.item.body, caseNumber: v.item.case_number ?? '' });
  }, [v]);
  if (!v || !f) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const p = v.item;
  const save = () => a.run(async () => (await api.post(`/api/pio/${id}`, { title: f.title, body: f.body, caseNumber: f.caseNumber || null }), r.reload()));
  const submit = () => a.run(async () => (await api.post(`/api/pio/${id}`, { title: f.title, body: f.body, caseNumber: f.caseNumber || null }), await api.post(`/api/pio/${id}/submit`, { note: note || null }), setNote(''), r.reload()));
  const decide = (approve: boolean) => a.run(async () => (await api.post(`/api/pio/${id}/decide`, { approve, note: note || null }), setNote(''), r.reload()));
  const release = () => a.run(async () => (await api.post(`/api/pio/${id}/release`, { public: pub }), r.reload()));
  const kindLabel = p.kind === 'release' ? 'Media release' : 'Case statement';
  return (
    <div className="ops stack">
      <PageHeader crumb={back('PIO', '/pio', navigate)} title={`${p.number} · ${p.title}`} sub={`${kindLabel}${p.case_number ? ` · case ${p.case_number}` : ''} · written by ${p.written_by_name}`} actions={<Badge tone={PIO_STATUS[p.status]![1]}>{PIO_STATUS[p.status]![0]}</Badge>} />
      <ErrorBox error={a.error} />
      {p.status === 'returned' && p.decision_note && (
        <Notice tone="bad">
          Sent back by {p.decided_by_name}: “{p.decision_note}”
        </Notice>
      )}
      {p.status === 'approved' && <Notice tone="ok">Approved by {p.decided_by_name} {when(p.decided_at)}{p.decision_note ? ` — “${p.decision_note}”` : ''}.</Notice>}
      {v.canEdit ? (
        <Card title={kindLabel}>
          <div className="stack">
            <div className="grid-2">
              <label>
                Title
                <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
              </label>
              <label>
                Case #
                <input value={f.caseNumber} onChange={(e) => setF({ ...f, caseNumber: e.target.value })} />
              </label>
            </div>
            <label>
              Text
              <textarea rows={12} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
            </label>
            <label>
              Note to command staff (optional)
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Needs to go out before the 6 o’clock news" />
            </label>
            <div className="row gap">
              <button className="btn primary" disabled={a.busy} onClick={submit}>
                Send for approval
              </button>
              <button className="btn" disabled={a.busy} onClick={save}>
                Save draft
              </button>
            </div>
          </div>
        </Card>
      ) : (
        <Card title={kindLabel}>
          <div className="pio-text">{p.body}</div>
        </Card>
      )}
      {v.canDecide && (
        <Card title="Command staff decision">
          <div className="stack sm">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (required to send it back)" aria-label="Decision note" />
            <div className="row gap">
              <button className="btn primary" disabled={a.busy} onClick={() => decide(true)}>
                Approve
              </button>
              <button className="btn warn" disabled={a.busy || !note.trim()} onClick={() => decide(false)}>
                Send back
              </button>
            </div>
          </div>
        </Card>
      )}
      {v.canRelease && (
        <Card title="Release it">
          <div className="stack sm">
            {p.kind === 'release' && (
              <label className="check">
                <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} /> Also post it on the agency’s public newsroom page
              </label>
            )}
            <div>
              <button className="btn primary" disabled={a.busy} onClick={release}>
                Mark released
              </button>
            </div>
          </div>
        </Card>
      )}
      <Card title="History">
        <ul className="pio-hist">
          {v.events.map((e: any, i: number) => (
            <li key={i}>
              <strong>{{ written: 'Written', edited: 'Edited', submitted: 'Sent for approval', approved: 'Approved', returned: 'Sent back', released: 'Released' }[e.action as 'written'] ?? e.action}</strong>
              <span className="small muted">
                {' '}
                · {e.by_name} · {when(e.at)}
              </span>
              {e.note && <div className="small">“{e.note}”</div>}
              {e.body && (
                <details>
                  <summary className="small">This version</summary>
                  <div className="pio-text small">{e.body}</div>
                </details>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ─── Tips ───────────────────────────────────────────────────────────────────
const TIP_STATUS: Record<string, [string, Tone]> = { new: ['New', 'bad'], assigned: ['Assigned', 'warn'], working: ['Being worked', 'info'], closed: ['Closed', 'ok'], unfounded: ['Unfounded', 'neutral'] };

export function TipsPage() {
  const { navigate } = useRouter();
  const { can } = useAuth();
  const [tab, setTab] = useState<'open' | 'mine' | 'closed' | 'all'>('open');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/tips?status=${tab}&q=${encodeURIComponent(q.trim())}`), [tab, q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="PIO"
        title="Tips"
        sub="Anonymous tips from the public — from BlueHQ’s tip page and your Jotform."
        actions={
          (can('admin.config') || can('tips.manage')) && (
            <button className="btn" onClick={() => navigate(at('/pio/settings'))}>
              Tip page & Jotform
            </button>
          )
        }
      />
      {r.data && (
        <div className="ops-kpis">
          <KPI n={r.data.counts.new} label="New" tone={r.data.counts.new ? 'bad' : undefined} />
          <KPI n={r.data.counts.open} label="Open" />
          <KPI n={r.data.counts.mine} label="Assigned to me" onClick={() => setTab('mine')} on={tab === 'mine'} />
        </div>
      )}
      <div className="row between wrap gap">
        <div className="seg-ctl" role="tablist" aria-label="Which tips">
          {(['open', 'mine', 'closed', 'all'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
              {{ open: 'Open', mine: 'Mine', closed: 'Closed', all: 'All' }[k]}
            </button>
          ))}
        </div>
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tip #, name, place, vehicle…" aria-label="Search tips" />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.tips.length ? (
        <EmptyState icon="search" title="No tips here" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Tip</th>
                <th>Received</th>
                <th>About</th>
                <th>Where</th>
                <th>Assigned</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.tips.map((t: any) => (
                <tr key={t.id} className={`clickable${t.status === 'new' ? ' tip-new' : ''}`} onClick={() => navigate(at(`/tips/${t.id}`))}>
                  <td className="mono">
                    {t.number}
                    {t.priority === 'high' && (
                      <div>
                        <Badge tone="bad">High</Badge>
                      </div>
                    )}
                  </td>
                  <td>{when(t.received_at)}</td>
                  <td>
                    <strong>{t.category ?? 'Tip'}</strong>
                    <div className="small muted tip-snip">{t.details}</div>
                  </td>
                  <td>{t.location ?? '—'}</td>
                  <td>{t.assigned_name ?? '—'}</td>
                  <td>
                    <Badge tone={TIP_STATUS[t.status]![1]}>{TIP_STATUS[t.status]![0]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function TipDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/tips/${id}`), [id]);
  const a = useAction();
  const [note, setNote] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [caseNo, setCaseNo] = useState<string | null>(null);
  const v = r.data;
  const act = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/tips/${id}`, body), setNote(''), r.reload()));
  if (!v) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const t = v.tip;
  const open = !['closed', 'unfounded'].includes(t.status);
  return (
    <div className="ops stack">
      <PageHeader crumb={back('Tips', '/tips', navigate)} title={`${t.number}${t.category ? ` · ${t.category}` : ''}`} sub={`Received ${when(t.received_at)} · ${t.source === 'jotform' ? 'Jotform' : 'BlueHQ tip page'}`} actions={<Badge tone={TIP_STATUS[t.status]![1]}>{TIP_STATUS[t.status]![0]}</Badge>} />
      <ErrorBox error={a.error} />
      <div className="grid2 top">
        <Card title="The tip">
          <dl className="kv">
            <dt>What they know</dt>
            <dd className="pre">{t.details}</dd>
            {t.location && (
              <>
                <dt>Where</dt>
                <dd>{t.location}</dd>
              </>
            )}
            {t.happened && (
              <>
                <dt>When</dt>
                <dd>{t.happened}</dd>
              </>
            )}
            {t.about && (
              <>
                <dt>Who</dt>
                <dd>{t.about}</dd>
              </>
            )}
            {t.vehicle && (
              <>
                <dt>Vehicle</dt>
                <dd>{t.vehicle}</dd>
              </>
            )}
            <dt>Tipster</dt>
            <dd>{t.contact_name || t.contact_phone || t.contact_email ? [t.contact_name, t.contact_phone, t.contact_email].filter(Boolean).join(' · ') : <span className="muted">Anonymous</span>}</dd>
          </dl>
        </Card>
        <Card title="Work the tip">
          <div className="stack sm">
            <label>
              Assigned to
              <select value={t.assigned_to ?? ''} disabled={a.busy} onChange={(e) => act({ assignedTo: e.target.value || null })}>
                <option value="">No one</option>
                {v.people.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="row gap wrap">
              <label className="check">
                <input type="checkbox" checked={t.priority === 'high'} disabled={a.busy} onChange={(e) => act({ priority: e.target.checked ? 'high' : 'normal' })} /> High priority
              </label>
              {open && t.status !== 'working' && (
                <button className="btn small" disabled={a.busy} onClick={() => act({ status: 'working' })}>
                  Mark being worked
                </button>
              )}
            </div>
            <label>
              Case #
              <div className="row gap">
                <input value={caseNo ?? t.case_number ?? ''} onChange={(e) => setCaseNo(e.target.value)} />
                <button className="btn small" disabled={a.busy || caseNo === null} onClick={() => act({ caseNumber: caseNo || null })}>
                  Save
                </button>
              </div>
            </label>
            <label>
              Add a note
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div>
              <button className="btn" disabled={a.busy || !note.trim()} onClick={() => act({ note })}>
                Add note
              </button>
            </div>
            {open ? (
              <>
                <label>
                  What came of it (to close)
                  <input value={closeNote} onChange={(e) => setCloseNote(e.target.value)} />
                </label>
                <div className="row gap">
                  <button className="btn primary" disabled={a.busy || !closeNote.trim()} onClick={() => act({ status: 'closed', closedNote: closeNote })}>
                    Close
                  </button>
                  <button className="btn" disabled={a.busy || !closeNote.trim()} onClick={() => act({ status: 'unfounded', closedNote: closeNote })}>
                    Unfounded
                  </button>
                </div>
              </>
            ) : (
              <p className="small">
                Closed: {t.closed_note}{' '}
                <button className="btn link small" onClick={() => act({ status: 'working' })}>
                  Reopen
                </button>
              </p>
            )}
          </div>
        </Card>
      </div>
      <Card title="History">
        <ul className="pio-hist">
          {v.events.map((e: any, i: number) => (
            <li key={i}>
              <strong>{e.action === 'received' ? 'Received' : e.action === 'note' ? 'Note' : e.action.charAt(0).toUpperCase() + e.action.slice(1)}</strong>
              <span className="small muted">
                {' '}
                · {e.by_name ?? 'Tip line'} · {when(e.at)}
              </span>
              {e.note && <div className="small pre">{e.note}</div>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ─── Settings: tip page, Jotform, CAD ───────────────────────────────────────
export function PioSettings() {
  const { navigate } = useRouter();
  const { can } = useAuth();
  const t = useLoad(() => api.get<any>('/api/tips/settings'), []);
  const c = useLoad(() => (can('admin.config') ? api.get<any>('/api/cad/settings') : Promise.resolve(null)), []);
  const a = useAction();
  const [jot, setJot] = useState<string | null>(null);
  const [cad, setCad] = useState<string | null>(null);
  const origin = location.origin;
  return (
    <div className="ops stack">
      <PageHeader crumb={back('Tips', '/tips', navigate)} title="Tip page, Jotform & CAD" sub="Where tips come in from, and how CAD calls reach BlueHQ." />
      <ErrorBox error={t.error ?? a.error} />
      {t.data && (
        <>
          <Card title="BlueHQ’s own tip page">
            <div className="stack sm">
              <label className="check">
                <input type="checkbox" checked={t.data.tipsPublic} disabled={a.busy} onChange={(e) => a.run(async () => (await api.post('/api/tips/settings', { tipsPublic: e.target.checked }), t.reload()))} /> Take tips on BlueHQ’s public tip page
              </label>
              {t.data.tipsPublic && (
                <p className="small">
                  Link for your website and social media:{' '}
                  <a href={t.data.publicPath} target="_blank" rel="noreferrer">
                    {origin}
                    {t.data.publicPath}
                  </a>
                </p>
              )}
            </div>
          </Card>
          <Card title="Your Jotform">
            <div className="stack sm">
              <p className="small">
                Keep your Jotform; its tips come straight into BlueHQ. Make a secret link here, then in Jotform open the form → <strong>Settings → Integrations → WebHooks</strong> and paste it in.
                {t.data.jotform.configured ? ` Link made ${when(t.data.jotform.createdAt)}.` : ''} {t.data.jotform.lastReceivedAt ? `Last tip from Jotform ${when(t.data.jotform.lastReceivedAt)}.` : ''}
              </p>
              {jot && (
                <div className="hook-link">
                  <code>{origin + jot}</code>
                  <span className="small muted">Copy it now — it isn’t shown again.</span>
                </div>
              )}
              <div>
                <button className="btn" disabled={a.busy} onClick={() => (!t.data.jotform.configured || confirm('Make a new link? The old one stops working.')) && a.run(async () => setJot((await api.post<any>('/api/tips/settings/link', {})).path))}>
                  {t.data.jotform.configured ? 'Make a new Jotform link' : 'Make the Jotform link'}
                </button>
              </div>
            </div>
          </Card>
        </>
      )}
      {c.data && (
        <Card title="CAD">
          <div className="stack sm">
            <p className="small">
              Your CAD vendor sends each call to this secret link (JSON: callNumber, nature, address, city, units, narrative, receivedAt). Calls from the last 24 hours show up when someone starts a critical notice, already filled in.
              {c.data.configured ? ` Link made ${when(c.data.createdAt)}.` : ''} {c.data.lastReceivedAt ? `Last call received ${when(c.data.lastReceivedAt)}.` : ''}
            </p>
            {cad && (
              <div className="hook-link">
                <code>{origin + cad}</code>
                <span className="small muted">Copy it now — it isn’t shown again.</span>
              </div>
            )}
            <div>
              <button className="btn" disabled={a.busy} onClick={() => (!c.data.configured || confirm('Make a new link? The old one stops working.')) && a.run(async () => setCad((await api.post<any>('/api/cad/settings/link', {})).path))}>
                {c.data.configured ? 'Make a new CAD link' : 'Make the CAD link'}
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
